<script setup lang="ts">
import type { RoutesNamesList } from '@typed-router/__routes'
import UAppBar from '@d/app/components/layout/UAppBar.vue'
import { useDeviceBreakpoint } from '@d/app/composables/useDeviceBreakpoint'
import { provideStateScope } from '@d/app/helper/shortcut'
import { ProjectGrantSymbol } from '@d/auth/helper/auth'
import { ProjectGrant } from '@d/auth/helper/permission/enum'
import UAlertBox from '@d/form/components/UAlertBox.vue'
import { mdiArrowLeft, mdiBookshelf, mdiCloud, mdiCog, mdiSearchWeb, mdiSecurity, mdiTrashCan } from '@mdi/js'
import { Pane, Splitpanes } from 'splitpanes'
import 'splitpanes/dist/splitpanes.css'
import '../style/main.scss'

const router = useRouter()
const projectgrants = inject(ProjectGrantSymbol)

if (!projectgrants?.value?.includes(ProjectGrant.manage)) {
  await router.replace({ name: 'index' })
}

// const layoutData = defineLayoutController('default', { rightDrawer: false })

// interface DrawerTab {
//   title: string
//   icon: string
//   id: string
// }

// const drawerTabs = computed(() => ([
//   { title: t('')}
// ] as DrawerTab[]))

const breakpoint = useDeviceBreakpoint()
const mobile = computed(() => breakpoint.isSmallerOrEqual('tablet'))
const DRAWER_EXPANDED_DEFAULT = 20
const navSize = ref(mobile.value ? 0 : DRAWER_EXPANDED_DEFAULT)
const mainSize = computed(() => 100 - navSize.value)

function toggleDrawer(): void {
  if (navSize.value <= 1) {
    if (mobile.value) {
      navSize.value = 100
    }
    else {
      navSize.value = DRAWER_EXPANDED_DEFAULT
    }
  }
  else {
    navSize.value = 0
  }
}

function closeDrawerIfMobile(): void {
  if (mobile.value) {
    navSize.value = 0
  }
}

const t = useI18n({
  useScope: 'local',
}).t

const route = useRoute('client-administration')

interface Tab {
  to: RoutesNamesList
  title: string
  icon?: string
  condition?: boolean
  color?: string
  textClass?: string
}

const tabs = computed<Tab[]>(() => [
  {
    to: 'client-administration',
    icon: mdiSecurity,
    title: t('tab.administration'),
  },
  {
    to: 'client-administration-storage',
    icon: mdiCloud,
    title: t('tab.storage'),
  },
  {
    to: 'client-administration-manual',
    icon: mdiBookshelf,
    title: t('tab.manualOverview'),
    condition: projectgrants?.value?.includes(ProjectGrant.manage),
  },
  {
    to: 'client-administration-search',
    icon: mdiSearchWeb,
    title: t('tab.searchOverview'),
    condition: projectgrants?.value?.includes(ProjectGrant.manage),
  },
  {
    to: 'client-administration-general',
    icon: mdiCog,
    title: t('tab.general'),
  },
  {
    to: 'client-administration-general-delete',
    icon: mdiTrashCan,
    title: t('tab.delete'),
    color: 'error',
    textClass: 'text-error',
    condition: projectgrants?.value?.includes(ProjectGrant.owner),
  },
  // {
  //   to: 'client-administration-info',
  //   title: t('tab.info'),
  // },
])

const tab = ref<string | null>(null)

watch(() => route.name, (value) => {
  if (tabs.value.find(tab => tab.to === value))
    tab.value = value
  else
    tab.value = null
}, { immediate: true })

watch(tab, (value) => {
  if (!value)
    return
  navigateTo({ name: value as any, params: route.params })
})

function navigateToTab(tab: Tab): void {
  navigateTo({ name: tab.to as any, params: route.params })
  closeDrawerIfMobile()
}

function goBack(): void {
  navigateTo({ name: 'client', params: { client: route.params.client } })
}

provideStateScope()
</script>

<template>
  <v-layout :class="{ 'is-mobile': mobile }">
    <UAppBar show-toggle-drawer :left-drawer-size="navSize" @toggle-drawer="toggleDrawer()" />
    <Splitpanes class="up-theme" style="max-height: 100vh;" @resize="navSize = $event[0].size;">
      <Pane :size="navSize" style="padding-top: 64px; display: flex;flex-direction: column;" class="drawer-pane">
        <div class="d-flex" style="height: 100%">
          <div style="flex:1; overflow-y: auto" class="">
            <v-list>
              <v-list-item :prepend-icon="mdiArrowLeft" @click="goBack">
                {{ t('goBack') }}
              </v-list-item>
              <v-divider />
              <template v-for="tab2 in tabs" :key="tab2.to">
                <v-list-item
                  v-if="tab2.condition === undefined || tab2.condition" :color="tab2.color ?? 'primary'"
                  :prepend-icon="tab2.icon"
                  :active="tab === tab2.to" @click="navigateToTab(tab2)"
                >
                  <span :class="tab2.textClass">{{ tab2.title }}</span>
                </v-list-item>
              </template>
            </v-list>
          </div>
        </div>
      </Pane>
      <Pane :size="mainSize" style="overflow: auto">
        <v-main style="display: flex; flex-direction: column; height: 100%">
          <div style="flex:1; overflow-x: hidden">
            <Suspense>
              <v-container style="height: 100%;">
                <slot />
              </v-container>
              <template #fallback>
                <v-skeleton-loader type="article" />
              </template>
            </Suspense>
          </div>
          <div>
            <UAlertBox />
          </div>
        </v-main>
      </Pane>
    </Splitpanes>
    <!--
    <div style="overflow: auto; width: 100%">
      <v-main style="display: flex;flex-direction: column; height: 100%">
        <div class="form-dialog" style="height: 100%;">
          <div style="flex:1; overflow-x: hidden">
            <Suspense>
              <slot />
              <template #fallback>
                <v-skeleton-loader type="article" />
              </template>
            </Suspense>
          </div>
        </div>
      </v-main>
    </div> -->
  </v-layout>
</template>

<style scoped>
.admin-tabs:deep(.v-slide-group__content) {
  align-items: center;
}

.link {
    text-transform: none !important;
}
</style>

<i18n lang="yaml">
de:
  tab:
    administration: Administration
    storage: Storage
    manualOverview: Handbücher
    general: Allgemein
    delete: Projekt löschen
    searchOverview: Indizierung
  goBack: Zurück zur Übersicht
en:
  tab:
    administration: Administration
    storage: Storage
    manualOverview: Manuals
    general: General
    delete: Delete Project
    searchOverview: Indexing
  goBack: Back to Overview
</i18n>
